import { Fragment, useCallback } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { Badge, Divider, Icon } from '@ovpn/ui';
import { NavigationMenu } from '@ovpn/ui/sidebar';

import type { SidebarSublink } from '@cloud/shared';

import { useMenuLinks } from './useMenuLinks';
import { useSidebarAnalytics } from './useSidebarAnalytics';

const menuSuffixBeta = (open: boolean) =>
    !open && <Icon size="sm" name="circle" color="secondary.base" />;

const menuSuffixNew = (open: boolean) =>
    !open && <Icon size="sm" name="circle" color="success.base" />;

function getMenuSuffix(menuChildLinks: SidebarSublink[] | null) {
    if (menuChildLinks?.some(t => t.decorator === 'new')) {
        return menuSuffixNew;
    }

    if (menuChildLinks?.some(t => t.decorator === 'beta')) {
        return menuSuffixBeta;
    }

    return undefined;
}

const decorators = {
    new: (
        <Badge size="xs" appearance="success">
            New
        </Badge>
    ),
    beta: (
        <Badge size="xs" appearance="secondary">
            Beta
        </Badge>
    ),
} as const;

export function SidebarLinks() {
    const { pathname } = useLocation();
    const { links, linksMap } = useMenuLinks();

    const { handleMainMenu, handleSubMenu } = useSidebarAnalytics(linksMap);

    const activeLinkMatcher = useCallback(
        (id: string) => {
            if (!id) {
                return false;
            }

            if (id === pathname) {
                return true;
            }

            const activeOn = linksMap.get(id)?.activeOn;

            if (activeOn instanceof RegExp) {
                return activeOn.test(pathname);
            }

            if (Array.isArray(activeOn)) {
                return activeOn.some(path => matchPath(pathname, { path }) !== null);
            }

            return false;
        },
        [pathname, linksMap],
    );

    return (
        <NavigationMenu match={activeLinkMatcher} onChange={handleSubMenu}>
            {links.map(menu => {
                const id = menu.to + (menu.children ? '-parent' : '');
                const childLinks = menu.children ? Object.values(menu.children) : null;

                return (
                    <Fragment key={id}>
                        {menu.divided && <Divider style={{ margin: '0 0 8px' }} />}
                        <NavigationMenu.Item
                            id={id}
                            as={childLinks === null ? Link : undefined}
                            to={menu.to}
                            icon={menu.icon}
                            title={menu.name}
                            suffix={getMenuSuffix(childLinks)}
                            onClick={() => {
                                handleMainMenu(menu.gtmId);
                            }}
                            data-testid="ovpn-sidebar-link"
                        >
                            {childLinks?.map(submenu => (
                                <NavigationMenu.Item
                                    as={Link}
                                    to={submenu.to}
                                    id={submenu.to}
                                    key={submenu.to}
                                    title={submenu.name}
                                    data-testid="ovpn-sidebar-sublink"
                                    suffix={submenu.decorator && decorators[submenu.decorator]}
                                />
                            ))}
                        </NavigationMenu.Item>
                    </Fragment>
                );
            })}
        </NavigationMenu>
    );
}
